import { useEffect, useMemo } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Box, TextField, Tooltip, Typography } from '@mui/material'
import { RootState } from 'store'
import { CrudAction, Entity, usePermissions } from 'utils/hooks'
import { getRangeTooltipMessage } from 'utils/helpers/validationFunctions'
import SpecVisualizer from '../SpecVisualizer/SpecVisualizer'
import { Container } from './AnalysisAttributesItem.styled'
import * as sxStyles from './AnalysisAttributesItem.styled'
import { Input } from '../../ui'

interface AnalysisAttributesItemProps {
  title: string
  value: number
  percentValue: number
  unit?: string
  color: string
  fontSize: number
  is_editable?: boolean
}

function AnalysisAttributesItem({
  title,
  value,
  percentValue,
  color,
  unit = '',
  fontSize,
  is_editable,
}: AnalysisAttributesItemProps) {
  const formContext = useFormContext()
  const hasFormContext = !!formContext?.control

  const { checkPermission } = usePermissions()
  const safeValue = value === null ? '' : value
  const sanitizeKey = (key: string) => key.replace(/\s+/g, '_').toLowerCase()
  const error =
    hasFormContext &&
    formContext.formState.errors?.attributes?.[sanitizeKey(title)]?.value
      ?.message

  useEffect(() => {
    if (hasFormContext) {
      formContext.setValue(`attributes.${sanitizeKey(title)}.value`, safeValue)
    }
  }, [safeValue])

  const validationRules = useSelector(
    (state: RootState) => state.validations.validationRules
  )

  const rule = validationRules?.[title]

  const watchedValue =
    hasFormContext &&
    useWatch({
      control: formContext.control,
      name: `attributes.${sanitizeKey(title)}.value`,
    })

  const warningMessage = useMemo(() => {
    const valueToCheck = Number(watchedValue)
    if (!rule || isNaN(valueToCheck)) return ''

    if (
      rule.warnAbove &&
      rule.hardMax &&
      valueToCheck > rule.warnAbove &&
      valueToCheck <= rule.hardMax
    ) {
      return `Above recommended range (${valueToCheck} > ${rule.warnAbove})`
    }

    if (
      rule.warnBelow &&
      rule.hardMin &&
      valueToCheck < rule.warnBelow &&
      valueToCheck >= rule.hardMin
    ) {
      return `Below recommended range (${valueToCheck} < ${rule.warnBelow})`
    }

    return ''
  }, [watchedValue, rule])

  return (
    <Container>
      <Box sx={sxStyles.boxWrapper}>
        <Typography fontSize={fontSize} sx={sxStyles.titleTypography}>
          {title}
        </Typography>
      </Box>
      <Box sx={sxStyles.boxWrapper}>
        {is_editable &&
        hasFormContext &&
        checkPermission(Entity.DELIVERY, CrudAction.UPDATE) ? (
          <Box sx={sxStyles.boxWrapper}>
            <Controller
              name={`attributes.${sanitizeKey(title)}.value`}
              control={formContext.control}
              defaultValue={safeValue ?? ''}
              render={({ field }) => (
                <Tooltip
                  title={
                    <Box>
                      {getRangeTooltipMessage(
                        validationRules,
                        title,
                        'range_min'
                      )}
                      {getRangeTooltipMessage(
                        validationRules,
                        title,
                        'range_max'
                      )}
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Input
                    size="small"
                    sx={{
                      ...(error
                        ? sxStyles.textFieldStyleOnError
                        : sxStyles.textFieldStyle),
                      ...(warningMessage &&
                        sxStyles.textFieldWarningStyleOnError),
                    }}
                    {...field}
                    value={field.value ?? ''}
                    error={!!error}
                    helperText={
                      error && warningMessage
                        ? `${warningMessage}`
                        : error || warningMessage || ''
                    }
                  />
                </Tooltip>
              )}
            />
          </Box>
        ) : value ? (
          <Box sx={sxStyles.boxWrapper}>
            <Typography fontSize={fontSize} sx={sxStyles.attributeValue}>
              {value}
              {unit}
            </Typography>
          </Box>
        ) : (
          <></>
        )}
      </Box>
      <SpecVisualizer value={value ? percentValue : undefined} color={color} />
    </Container>
  )
}

export default AnalysisAttributesItem
